// src/pages/DownloadCourse.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Alert,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArchiveIcon from '@mui/icons-material/Archive';
import DownloadIcon from '@mui/icons-material/Download';
import { useAxiosAuth } from '../hooks/useAxiosAuth';

export default function DownloadCourse() {
  const axiosInstance = useAxiosAuth();

  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [downloadingWebsite, setDownloadingWebsite] = useState(false);
  const [exportingPackage, setExportingPackage] = useState(false);
  const [importingPackage, setImportingPackage] = useState(false);
  const [selectedPackageFile, setSelectedPackageFile] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [importedCourseResult, setImportedCourseResult] = useState(null);

  const loadCourses = useCallback(async () => {
    try {
      const resp = await axiosInstance.get('/courses/');
      setCourses(resp.data);
    } catch (err) {
      console.error('Failed to load courses', err);
      setErrorMessage('Failed to load courses');
    }
  }, [axiosInstance]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const selectedCourseObj = useMemo(
    () => courses.find((course) => String(course.id) === String(selectedCourse)),
    [courses, selectedCourse]
  );

  const startBlobDownload = (data, disposition, fallbackFilename) => {
    let filename = fallbackFilename;
    if (disposition) {
      const match = disposition.match(/filename="([^"]+)"/i);
      if (match && match[1]) filename = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleWebsiteDownload = async () => {
    if (!selectedCourse) return;

    setDownloadingWebsite(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const resp = await axiosInstance.get(`/download_course/${selectedCourse}`, {
        responseType: 'blob',
      });
      startBlobDownload(
        resp.data,
        resp.headers['content-disposition'],
        'course.zip'
      );
    } catch (err) {
      console.error('Website download failed', err);
      setErrorMessage('Failed to download website zip');
    } finally {
      setDownloadingWebsite(false);
    }
  };

  const handleTransferExport = async () => {
    if (!selectedCourse) return;

    setExportingPackage(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      const resp = await axiosInstance.get(
        `/export_course_package/${selectedCourse}`,
        { responseType: 'blob' }
      );
      startBlobDownload(
        resp.data,
        resp.headers['content-disposition'],
        'course-transfer-package.zip'
      );
    } catch (err) {
      console.error('Transfer package export failed', err);
      setErrorMessage('Failed to export transfer package');
    } finally {
      setExportingPackage(false);
    }
  };

  const handlePackageFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedPackageFile(file);
    setErrorMessage('');
    setSuccessMessage('');
    setImportedCourseResult(null);
  };

  const handleTransferImport = async () => {
    if (!selectedPackageFile) return;

    setImportingPackage(true);
    setErrorMessage('');
    setSuccessMessage('');
    setImportedCourseResult(null);
    try {
      const formData = new FormData();
      formData.append('package', selectedPackageFile);
      const resp = await axiosInstance.post('/import_course_package', formData);

      setSuccessMessage(resp.data.message || 'Transfer package imported successfully');
      setImportedCourseResult(resp.data);
      setSelectedCourse(resp.data.course_id ?? '');
      await loadCourses();
    } catch (err) {
      console.error('Transfer package import failed', err);
      setErrorMessage(
        err?.response?.data?.detail || 'Failed to import transfer package'
      );
    } finally {
      setImportingPackage(false);
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          Course Transfer
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Download the published course website, export a portable transfer package,
          or import a package into a brand-new course.
        </Typography>

        <Stack spacing={2} sx={{ mb: 3 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}
          {importedCourseResult?.course_id && (
            <Alert severity="info">
              Imported course ID: {importedCourseResult.course_id}
              {importedCourseResult.course_website_url
                ? ` • ${importedCourseResult.course_website_url}`
                : ''}
            </Alert>
          )}
        </Stack>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="course-label">Course</InputLabel>
          <Select
            labelId="course-label"
            value={selectedCourse}
            label="Course"
            onChange={e => setSelectedCourse(e.target.value)}
          >
            <MenuItem value="">-- Select a Course --</MenuItem>
            {courses.map(c => (
              <MenuItem key={c.id} value={c.id}>
                {c.subject}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCourseObj?.course_website_url && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Course site:</Typography>
            <Button
              size="small"
              variant="outlined"
              href={selectedCourseObj.course_website_url}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<LaunchIcon fontSize="small" />}
            >
              View in new tab
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCopy(selectedCourseObj.course_website_url)}
              endIcon={<ContentCopyIcon fontSize="small" />}
            >
              {copied ? 'Copied!' : 'Copy URL'}
            </Button>
          </Box>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 4 }}
        >
          <Button
            variant="contained"
            onClick={handleWebsiteDownload}
            disabled={downloadingWebsite || !selectedCourse}
            startIcon={
              downloadingWebsite ? <CircularProgress size={20} /> : <DownloadIcon />
            }
          >
            Download Website ZIP
          </Button>
          <Button
            variant="outlined"
            onClick={handleTransferExport}
            disabled={exportingPackage || !selectedCourse}
            startIcon={
              exportingPackage ? <CircularProgress size={20} /> : <ArchiveIcon />
            }
          >
            Export Transfer Package
          </Button>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Import Transfer Package
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Import creates a new course and uploads the package contents through the
          transfer API.
        </Typography>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
            Choose Transfer Package
            <input
              hidden
              type="file"
              accept=".zip,application/zip"
              aria-label="Transfer package file"
              onChange={handlePackageFileChange}
            />
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {selectedPackageFile ? selectedPackageFile.name : 'No package selected'}
          </Typography>
          <Button
            variant="contained"
            onClick={handleTransferImport}
            disabled={importingPackage || !selectedPackageFile}
            startIcon={
              importingPackage ? <CircularProgress size={20} /> : <UploadFileIcon />
            }
          >
            Import Transfer Package
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}
